import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Modal } from "@/components/ui-bits";
import { SPORT_THEMES, THEME_KEYS } from "@/lib/brand";

const NewStudio = ({ onClose, onCreated }) => {
  const [f, setF] = useState({ businessName: "", theme: "gym" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const r = await api.post("/me/businesses", f); onCreated(r.data); }
    catch (ex) { toast.error(errMsg(ex)); setBusy(false); }
  };
  return (
    <Modal title="NEW STUDIO" sub="A fully separate workspace — its own clients, schedule, brand and portal link" onClose={onClose} testId="new-studio-modal">
      <form onSubmit={submit}>
        <label>Business name</label><input required minLength={2} maxLength={60} autoFocus value={f.businessName} onChange={(e) => setF({ ...f, businessName: e.target.value })} placeholder="Rhino Fitness North" data-testid="new-studio-name-input" />
        <label>Sport theme</label>
        <select value={f.theme} onChange={(e) => setF({ ...f, theme: e.target.value })} data-testid="new-studio-theme-select">{THEME_KEYS.map((k) => <option key={k} value={k}>{SPORT_THEMES[k].label}</option>)}</select>
        <div className="ss" style={{ marginTop: 6 }}>You'll sign in to both studios with the same email and password and switch from the top bar.</div>
        <div className="mfoot"><button type="button" className="btn g" onClick={onClose}>Cancel</button><button className="btn" disabled={busy} data-testid="new-studio-create-button">{busy ? "Creating…" : "Create studio"}</button></div>
      </form>
    </Modal>
  );
};

export const BusinessSwitcher = ({ name }) => {
  const { adopt } = useAuth();
  const nav = useNavigate();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(false);
  const ref = useRef();
  useEffect(() => { api.get("/me/businesses").then((r) => setList(r.data)).catch(() => {}); }, []);
  useEffect(() => { const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }; document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h); }, []);
  const enter = (data) => { sessionStorage.setItem("ip_biz", data.business.slug); adopt(data.token, data.user); window.dispatchEvent(new Event("brand-changed")); setOpen(false); setModal(false); nav("/admin", { replace: true }); window.location.reload(); };
  const switchTo = async (b) => { if (b.current) return setOpen(false); try { const r = await api.post("/me/switch", { businessId: b.id }); toast.success(`Switched to ${b.name}`); enter(r.data); } catch (ex) { toast.error(errMsg(ex)); } };
  const current = list.find((b) => b.current);
  return (
    <div className="switcher" ref={ref} data-testid="business-switcher">
      <button type="button" className="switcher-btn" onClick={() => setOpen((o) => !o)} aria-expanded={open} data-testid="business-switcher-button">
        <b data-testid="admin-business-name">{(current?.name || name || "\u00a0").toUpperCase()}</b>
        <small>ADMIN CONSOLE{list.length > 1 ? ` · ${list.length} STUDIOS` : ""}</small>
        <ChevronDown size={16} className={`chev ${open ? "up" : ""}`} />
      </button>
      {open && (
        <div className="switcher-menu" data-testid="business-switcher-menu">
          <div className="ss" style={{ padding: "6px 12px 4px" }}>Your studios</div>
          {list.map((b) => (
            <button type="button" key={b.id} className={`switcher-item ${b.current ? "on" : ""}`} onClick={() => switchTo(b)} data-testid={`switch-business-${b.slug}`}>
              <span className="logo" style={{ width: 26, height: 26, fontSize: 11 }}>{b.name.slice(0, 2).toUpperCase()}</span>
              <span className="sb"><span className="st" style={{ fontSize: 13 }}>{b.name}</span><span className="ss">/b/{b.slug}</span></span>
              {b.current && <Check size={16} style={{ color: "var(--accent)" }} />}
            </button>
          ))}
          <button type="button" className="switcher-item add" onClick={() => { setOpen(false); setModal(true); }} data-testid="create-studio-button"><Plus size={16} /> Create another studio</button>
        </div>
      )}
      {modal && <NewStudio onClose={() => setModal(false)} onCreated={(d) => { toast.success(`${d.business.name} created`); enter(d); }} />}
    </div>
  );
};
